import {
  IWebhook,
  IWebhookDictionary
} from 'shared/interfaces/webhook.interface';
import { http } from 'shared/utils/axios';
import { getTokenFromLocalStorage } from 'shared/utils/local-storage';

type GroupedWebhookEvent = {
  type: string;
  label: string;
  description: string;
};

type GroupedWebhookDictionary = Record<string, GroupedWebhookEvent[]>;

const normalizeWebhookDictionary = (
  data: IWebhookDictionary[] | GroupedWebhookDictionary | unknown
): IWebhookDictionary[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === 'object') {
    const eventMap = new Map<string, IWebhookDictionary>();

    Object.entries(data as GroupedWebhookDictionary).forEach(
      ([category, events]) => {
        const normalizedCategory = category.toLowerCase();

        (events || []).forEach((event) => {
          const existing = eventMap.get(event.type);

          if (existing) {
            if (!existing.categories.includes(normalizedCategory)) {
              existing.categories.push(normalizedCategory);
            }
          } else {
            eventMap.set(event.type, {
              eventSystemName: event.type,
              eventReadableName: event.label,
              eventDescription: event.description,
              categories: [normalizedCategory]
            });
          }
        });
      }
    );

    return Array.from(eventMap.values());
  }

  return [];
};

class WebhooksService {
  private static instance: WebhooksService;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  static getInstance(): WebhooksService {
    if (!WebhooksService.instance) {
      WebhooksService.instance = new WebhooksService();
    }

    return WebhooksService.instance;
  }

  getAllWebhooks = async () => {
    const { data } = await http<IWebhook[]>({
      url: '/webhooks',
      method: 'GET'
    });
    return data;
  };

  public deleteWebhook = async (webhookId: string) => {
    await http({
      url: `/webhooks/${webhookId}`,
      method: 'DELETE'
    });

    return webhookId;
  };

  getWebhooksDictionary = async () => {
    const { data } = await http<
      IWebhookDictionary[] | GroupedWebhookDictionary
    >({
      url: '/webhooks/actions/getDictionary',
      method: 'GET',
      params: { access_token: getTokenFromLocalStorage() }
    });
    return normalizeWebhookDictionary(data);
  };
  public createWebhook = async (payload: IWebhook) => {
    const { data } = await http<IWebhook>({
      url: '/webhooks',
      method: 'POST',
      data: payload
    });

    return data;
  };
  public updateWebhook = async (payload: { id: string; data: IWebhook }) => {
    const { data } = await http<IWebhook>({
      url: `/webhooks/${payload.id}`,
      method: 'PUT',
      data: payload.data
    });

    return data;
  };
}

const instance = WebhooksService.getInstance();

export default instance;
